import localforage from 'localforage'
import { MetaSchema } from './types'

export const PREVIEW_CHUNK_ROW_COUNT = 100

export const PREVIEW_STORE_KEYS = {
  HEADERS: 'headers',
  CHUNK_COUNT: 'chunk_count',
  ROW_COUNT: 'row_count'
}

export const getPreviewChunkKey = (index) => `row_chunk_${index}`

/** 項目情報モーダルのサンプル欄に表示する区分値の最大件数 */
export const FIELD_INFO_SAMPLE_MAX_COUNT = 10

/**
 * 項目情報モーダルの「サンプル」表示文字列を組み立てる。区分マスターの区分値（valueMappingColumns）があればそれを優先して最大 FIELD_INFO_SAMPLE_MAX_COUNT
 * 件まで「、」区切りで並べ、超過分は「…」で省略する。区分値がない場合は AppMaster のサンプル値（chrofyColumnSample）を返し、どちらも無ければ空文字を返す（サンプル欄は非表示）。
 *
 * @param {{ valueMappingColumns?: string[]; chrofyColumnSample?: string }} col
 * @returns {string}
 */
export function buildFieldInfoSampleText(col) {
  const values = (col?.valueMappingColumns ?? []).filter((v) => v != null && v !== '')
  if (values.length > 0) {
    const head = values.slice(0, FIELD_INFO_SAMPLE_MAX_COUNT).join('、')
    return values.length > FIELD_INFO_SAMPLE_MAX_COUNT ? `${head}…` : head
  }
  return col?.chrofyColumnSample ?? ''
}

/**
 * Snake_case キーを camelCase に変換したオブジェクトを返す（1階層のみ）
 *
 * @param {Record<string, unknown>} obj
 * @returns {Record<string, unknown>}
 */
export function _snakeToCamel(obj) {
  if (obj === null || typeof obj !== 'object') return obj
  const out = {}
  for (const [k, v] of Object.entries(obj)) {
    const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
    out[camel] = v
  }
  return out
}

/**
 * POST 等で返る JSON のキーを snake_case → camelCase に揃える（api-patterns: ストア・画面は camelCase）。 `deep: false` のときは 1
 * オブジェクト分のみ変換し配下はそのまま（IF02095 部分正規化用）。
 *
 * @param {unknown} raw
 * @param {{ deep?: boolean }} [options]
 * @returns {unknown}
 */
export function normalizeDeepKeysToCamel(raw, options) {
  const deep = options?.deep !== false
  if (raw === null || raw === undefined) {
    return raw
  }
  if (Array.isArray(raw)) {
    return deep ? raw.map((item) => normalizeDeepKeysToCamel(item, options)) : raw
  }
  if (typeof raw !== 'object') {
    return raw
  }
  const obj = /** @type {Record<string, unknown>} */ (raw)
  const out = {}
  for (const [k, v] of Object.entries(obj)) {
    const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
    out[camel] = deep ? normalizeDeepKeysToCamel(v, options) : v
  }
  return out
}

export const createPreviewStore = (taskKey) => {
  return localforage.createInstance({
    name: `validation_preview_${taskKey}`,
    driver: [localforage.INDEXEDDB]
  })
}

export async function savePreviewCsvToStore(fileStore, rows, headers) {
  const normalizedRows = Array.isArray(rows) ? rows : []
  const normalizedHeaders = Array.isArray(headers) ? headers : []

  await fileStore.clear()
  await fileStore.setItem(PREVIEW_STORE_KEYS.HEADERS, normalizedHeaders)
  await fileStore.setItem(PREVIEW_STORE_KEYS.ROW_COUNT, normalizedRows.length)

  let chunkIndex = 0
  for (let startIndex = 0; startIndex < normalizedRows.length; startIndex += PREVIEW_CHUNK_ROW_COUNT) {
    const chunkRows = normalizedRows.slice(startIndex, startIndex + PREVIEW_CHUNK_ROW_COUNT)
    await fileStore.setItem(getPreviewChunkKey(chunkIndex), chunkRows)
    chunkIndex += 1
  }

  await fileStore.setItem(PREVIEW_STORE_KEYS.CHUNK_COUNT, chunkIndex)
}

export async function loadPreviewCsvRowsRange(fileStore, startIndex, endIndex) {
  const safeStartIndex = Math.max(0, Number(startIndex) || 0)
  const safeEndIndex = Math.max(safeStartIndex, Number(endIndex) || 0)
  const chunkCount = (await fileStore.getItem(PREVIEW_STORE_KEYS.CHUNK_COUNT)) || 0
  if (chunkCount === 0) return []

  const startChunkIndex = Math.floor(safeStartIndex / PREVIEW_CHUNK_ROW_COUNT)
  const endChunkIndex = Math.floor(safeEndIndex / PREVIEW_CHUNK_ROW_COUNT)
  const rows = []

  for (let chunkIndex = startChunkIndex; chunkIndex <= endChunkIndex; chunkIndex += 1) {
    const chunkRows = (await fileStore.getItem(getPreviewChunkKey(chunkIndex))) || []
    if (Array.isArray(chunkRows)) rows.push(...chunkRows)
  }

  const offsetStart = safeStartIndex - startChunkIndex * PREVIEW_CHUNK_ROW_COUNT
  return rows.slice(offsetStart, offsetStart + (safeEndIndex - safeStartIndex + 1))
}

export async function loadPreviewCsvFromStore(fileStore) {
  const headers = (await fileStore.getItem(PREVIEW_STORE_KEYS.HEADERS)) || []
  const rowCount = (await fileStore.getItem(PREVIEW_STORE_KEYS.ROW_COUNT)) || 0
  const rows = await loadPreviewCsvRowsRange(fileStore, 0, Math.max(0, rowCount - 1))
  return { headers, rows, rowCount }
}

export async function loadPreviewCsvMetaFromStore(fileStore) {
  const headers = (await fileStore.getItem(PREVIEW_STORE_KEYS.HEADERS)) || []
  const rowCount = (await fileStore.getItem(PREVIEW_STORE_KEYS.ROW_COUNT)) || 0
  const chunkCount = (await fileStore.getItem(PREVIEW_STORE_KEYS.CHUNK_COUNT)) || 0
  return { headers, rowCount, chunkCount }
}

export async function clearPreviewStore(taskKey) {
  if (!taskKey) return
  await localforage.dropInstance({
    name: `validation_preview_${taskKey}`
  })
}

/**
 * 列が数値型かどうかを返す。優先順位/合算タブは数値列のみ表示するために使用。 CHROFY のデータ型は `string` / `int` / `decimal` / `date` の 4 種（汎用CHROFY項目.md 参照）。
 * このうち数値型は `int` と `decimal`。chrofyColumnDataType がこれらと一致する場合に true。
 *
 * @param {{ chrofyColumnDataType?: string | null }} col - GenericColumns の要素
 * @returns {boolean}
 */
export function isNumericColumn(col) {
  const dt = col?.chrofyColumnDataType
  if (dt == null || typeof dt !== 'string') return false
  const lower = dt.trim().toLowerCase()
  return lower === 'int' || lower === 'decimal'
}

/**
 * 複数マッピングの優先順位を ↑↓ で変更する UI を表示するか。 数値列は priority モードかつ items が 2 件以上のときのみ。非数値列は items が 2 件以上なら常に表示。
 *
 * @param {{ items?: unknown[]; mode?: string } | null | undefined} entry
 * @param {{ chrofyColumnDataType?: string | null }} col
 * @returns {boolean}
 */
export function showMappingReorderButtons(entry, col) {
  const items = entry?.items
  if (!Array.isArray(items) || items.length < 2) return false
  if (isNumericColumn(col)) return entry.mode === 'priority'
  return true
}

/**
 * IF02083 の genericMappingTask から「■ {データ種類名} - {データ分類}」形式の1行を組み立てる。名称が片方のみのときは「■ {ある方}」。
 *
 * @param {import('./types').Meta | null | undefined} meta
 * @returns {string}
 */
export function buildDataTypeInfoHeadingLine(meta) {
  const task = meta?.genericMappingTask
  const kind = (task?.genericTaskKindName != null ? String(task.genericTaskKindName).trim() : '') || ''
  const classification = (task?.genericTaskName != null ? String(task.genericTaskName).trim() : '') || ''
  if (kind && classification && kind !== classification) return `■ ${kind} - ${classification}`
  const single = kind || classification
  return single ? `■ ${single}` : '■'
}

/**
 * 進捗タイトル等で用いるデータ分類の表示名。メタのタスクコードと一致すれば genericTaskName を優先。一致しない・名称なしは code をそのまま返す。
 *
 * @param {import('./types').Meta | null | undefined} meta
 * @param {string | null | undefined} categoryCode
 * @returns {string}
 */
export function getDataClassificationDisplayName(meta, categoryCode) {
  const code = categoryCode != null ? String(categoryCode).trim() : ''
  if (!code) return ''
  const task = meta?.genericMappingTask
  if (task && String(task.genericTaskCode ?? '') === code) {
    const name = task.genericTaskName
    if (name != null && String(name).trim() !== '') return String(name).trim()
  }
  const cols = meta?.genericChrofyColumns ?? []
  const first = cols.find((c) => String(c.genericTaskCode ?? '') === code)
  const extra =
    first && typeof first === 'object'
      ? /** @type {{ genericDataKindName?: string }} */ (first).genericDataKindName
      : undefined
  if (extra != null && String(extra).trim() !== '') return String(extra).trim()
  return code
}

/**
 * IF02083 レスポンスを画面用 Meta に変換
 *
 * @param {unknown} raw
 * @returns {import('./types').Meta}
 */
export function _transformMetaResponse(raw) {
  const body = typeof raw === 'object' && raw !== null ? raw : {}
  const r = _snakeToCamel(/** @type {Record<string, unknown>} */ (body))
  if (r.genericMappingTask && typeof r.genericMappingTask === 'object') {
    r.genericMappingTask = _snakeToCamel(/** @type {Record<string, unknown>} */ (r.genericMappingTask))
  }
  if (Array.isArray(r.genericChrofyColumns)) {
    r.genericChrofyColumns = r.genericChrofyColumns.map((c) =>
      _snakeToCamel(/** @type {Record<string, unknown>} */ (c))
    )
  }
  if (r.dataCreateTaskInfo && typeof r.dataCreateTaskInfo === 'object') {
    r.dataCreateTaskInfo = _snakeToCamel(/** @type {Record<string, unknown>} */ (r.dataCreateTaskInfo))
  }
  if (Array.isArray(r.conversionTableList)) {
    r.conversionTableList = r.conversionTableList.map((t) => _snakeToCamel(/** @type {Record<string, unknown>} */ (t)))
  }
  return MetaSchema.parse(r)
}

/**
 * IF02084 レスポンスをファイル種別配列に変換（camelCase） APIは各要素に mapping_file_type を持つ配列を直接返す。 ファイル種別（file classification）は各要素の
 * mapping_file_type で識別する。 column_list → headers、unique_values_map → columnUniqueValues に正規化する（FileTypeInfoSchema
 * に合わせる）。
 *
 * @param {unknown} raw
 * @returns {import('./types').FileTypeInfo[]}
 */
export function _transformFileTypesResponse(raw) {
  const arr = Array.isArray(raw) ? raw : []
  return arr.map((item) => {
    const o = _snakeToCamel(/** @type {Record<string, unknown>} */ (item))
    if (o.columnList != null && o.headers == null) {
      o.headers = o.columnList
    }
    if (o.uniqueValuesMap != null && typeof o.uniqueValuesMap === 'object' && o.columnUniqueValues == null) {
      o.columnUniqueValues = o.uniqueValuesMap
    }
    return o
  })
}

/**
 * Object 以外を空オブジェクトとして扱う
 *
 * @param {unknown} value
 * @returns {Record<string, unknown>}
 */
function _asRecord(value) {
  return value !== null && typeof value === 'object' ? /** @type {Record<string, unknown>} */ (value) : {}
}

/**
 * 候補キーから最初に見つかった非 nullish 値を返す
 *
 * @param {Record<string, unknown>} obj
 * @param {...string} keys
 * @returns {unknown}
 */
function _pick(obj, ...keys) {
  for (const key of keys) {
    const value = obj[key]
    if (value != null) return value
  }
  return undefined
}

/**
 * 指定キーを除いたオブジェクトを返す
 *
 * @param {Record<string, unknown>} obj
 * @param {string[]} keys
 * @returns {Record<string, unknown>}
 */
function _omitKeys(obj, keys) {
  const next = { ...obj }
  for (const key of keys) {
    delete next[key]
  }
  return next
}

/**
 * 候補キーから配列を取得する
 *
 * @param {Record<string, unknown>} obj
 * @param {...string} keys
 * @returns {unknown[]}
 */
function _pickArray(obj, ...keys) {
  const value = _pick(obj, ...keys)
  return Array.isArray(value) ? value : []
}

/**
 * Simple mapping を画面表示用 item に変換
 *
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
function _simpleToItem(mappingInfo) {
  const simpleMapping = _asRecord(mappingInfo.simple_mapping)
  return {
    type: 'simple',
    fileType: simpleMapping.mapping_file_type,
    field: simpleMapping.mapping_column_name
  }
}

/**
 * Fixed mapping を画面表示用 item に変換
 *
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
function _fixedToItem(mappingInfo) {
  return {
    type: 'fixed',
    constant: mappingInfo.constant,
    value: mappingInfo.constant
  }
}

/**
 * Conversion_table mapping を画面表示用 item に変換
 *
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
function _conversionTableToItem(mappingInfo) {
  const conversionTable = _asRecord(mappingInfo.conversion_table)
  const source = Object.keys(conversionTable).length > 0 ? conversionTable : mappingInfo
  const fileType = _pick(source, 'mapping_file_type', 'mappingFileType') ?? ''
  const field = _pick(source, 'mapping_column_name', 'mappingColumnName') ?? ''
  const conversionTableKey =
    _pick(mappingInfo, 'conversion_table_key') ?? _pick(conversionTable, 'conversion_table_key') ?? ''

  return {
    type: 'conversion_table',
    fileType,
    field,
    conversionTableKey,
    mapping_info: mappingInfo
  }
}

/**
 * Arithmetic mapping を画面表示用 item に変換
 *
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
function _arithmeticToItem(mappingInfo) {
  const rest = _omitKeys(mappingInfo, ['four_arithmetic_expressions', 'form_state'])
  return {
    type: 'FourArithmeticExpressions',
    mapping_info: {
      ...rest,
      mappingType: 'FourArithmeticExpressions',
      fourArithmeticExpressions: {
        expressionAst: _pick(mappingInfo, 'four_arithmetic_expressions') ?? ''
      },
      formState: _pick(mappingInfo, 'form_state') ?? null
    }
  }
}

/**
 * 単一条件行を画面表示用に正規化
 *
 * @param {unknown} row
 * @returns {Record<string, unknown>}
 */
function _normalizeConditionRow(row) {
  const rawRow = _asRecord(row)
  const rowOut = _omitKeys(rawRow, ['conditional_expression', 'ConditionalExpression', 'form_state'])

  rowOut.conditionalExpression =
    _pick(rawRow, 'conditional_expression', 'ConditionalExpression', 'conditionalExpression') ?? ''
  rowOut.value = _pick(rawRow, 'value', 'Value') ?? ''

  return rowOut
}

/**
 * 単一条件の formState を抽出
 *
 * @param {unknown[]} rows
 * @returns {unknown}
 */
function _extractConditionFormState(rows) {
  return (
    rows.find((row) => _asRecord(row).form_state != null)?.form_state ??
    rows.find((row) => _asRecord(row).formState != null)?.formState ??
    null
  )
}

/**
 * Condition mapping を画面表示用 item に変換
 *
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
function _conditionToItem(mappingInfo) {
  const rows = _pickArray(mappingInfo, 'single_condition')
  const rest = _omitKeys(mappingInfo, ['single_condition', 'form_state'])

  return {
    type: 'SingleCondition',
    mapping_info: {
      ...rest,
      mappingType: 'SingleCondition',
      singleCondition: rows.map(_normalizeConditionRow),
      formState: _extractConditionFormState(rows)
    }
  }
}

/**
 * 条件式を画面表示用に正規化
 *
 * @param {unknown} expression
 * @returns {Record<string, unknown>}
 */
function _normalizeConditionalExpression(expression) {
  const rawExpression = _asRecord(expression)
  const expressionOut = _omitKeys(rawExpression, ['conditional_expression', 'ConditionalExpression', 'form_state'])

  expressionOut.conditionalExpression =
    _pick(rawExpression, 'conditional_expression', 'ConditionalExpression', 'conditionalExpression') ?? ''

  return expressionOut
}

/**
 * 条件セットを画面表示用に正規化
 *
 * @param {unknown} set
 * @returns {Record<string, unknown>}
 */
function _normalizeConditionSet(set) {
  const rawSet = _asRecord(set)
  const exprList = _pickArray(
    rawSet,
    'conditional_expression_list',
    'ConditionalExpressionList',
    'conditionalExpressionList'
  )
  const setOut = _omitKeys(rawSet, [
    'join_condition',
    'JoinCondition',
    'conditional_expression_list',
    'ConditionalExpressionList'
  ])

  setOut.joinCondition = _pick(rawSet, 'join_condition', 'JoinCondition', 'joinCondition') ?? 'and'
  setOut.conditionalExpressionList = exprList.map(_normalizeConditionalExpression)

  return setOut
}

/**
 * 複数条件行の formState を抽出
 *
 * @param {unknown[]} rows
 * @returns {unknown}
 */
function _extractMultiConditionFormState(rows) {
  return (
    rows
      .flatMap((row) => _pickArray(_asRecord(row), 'condition_set', 'ConditionSet'))
      .flatMap((set) =>
        _pickArray(
          _asRecord(set),
          'conditional_expression_list',
          'ConditionalExpressionList',
          'conditionalExpressionList'
        )
      )
      .find((expression) => _asRecord(expression).form_state != null)?.form_state ?? null
  )
}

/**
 * 複数条件行を画面表示用に正規化
 *
 * @param {unknown} row
 * @returns {Record<string, unknown>}
 */
function _normalizeMultiConditionRow(row) {
  const rawRow = _asRecord(row)
  const conditionSet = _pickArray(rawRow, 'condition_set', 'ConditionSet')
  const rowOut = _omitKeys(rawRow, ['condition_set', 'ConditionSet', 'value', 'Value'])

  rowOut.conditionSet = conditionSet.map(_normalizeConditionSet)
  rowOut.value = _pick(rawRow, 'value', 'Value') ?? ''

  return rowOut
}

/**
 * Condition_multiple mapping を画面表示用 item に変換
 *
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
function _multiConditionToItem(mappingInfo) {
  const rows = _pickArray(mappingInfo, 'multi_condition')
  const rest = _omitKeys(mappingInfo, ['multi_condition', 'form_state'])

  return {
    type: 'multi_condition',
    mapping_info: {
      ...rest,
      mappingType: 'multi_condition',
      multiCondition: rows.map(_normalizeMultiConditionRow),
      formState: _extractMultiConditionFormState(rows)
    }
  }
}

/**
 * Value_conversion mapping を画面表示用 item に変換
 *
 * @param {Record<string, unknown>} mappingInfo
 * @param {string} [mappingType]
 * @returns {Record<string, unknown>}
 */
function _valueMappingToItem(mappingInfo, mappingType = 'value_conversion') {
  const valueMapping = Array.isArray(mappingInfo.value_mapping)
    ? mappingInfo.value_mapping[0]
    : mappingInfo.value_mapping
  const valueMappingObject = _asRecord(valueMapping)
  const fileType = _pick(valueMappingObject, 'mapping_file_type', 'mappingFileType') ?? ''
  const field = _pick(valueMappingObject, 'mapping_column_name', 'mappingColumnName') ?? ''
  const mappingList = _pickArray(valueMappingObject, 'mapping_list', 'mappingList', 'MappingList')
  const valueMappingUiType = mappingType === 'value_conversion_manual' ? 'manual' : 'dnd'
  const itemType = valueMappingUiType === 'manual' ? 'value_mapping' : 'value_mapping_dnd'

  return {
    type: itemType,
    fileType,
    field,
    mapping_info: {
      MappingType: 'ValueMapping',
      _valueMappingUiType: valueMappingUiType,
      ValueMapping: {
        MappingFileType: fileType,
        MappingColumnName: field,
        MappingList: mappingList
      }
    }
  }
}

/**
 * Date_conversion の format を UI 用文字列に変換
 *
 * @param {unknown} format
 * @returns {string}
 */
function _dateFormatToUiString(format) {
  if (typeof format === 'string') return format
  const formatObject = _asRecord(format)
  const year = _pick(formatObject, 'year', 'Year')
  const month = _pick(formatObject, 'month', 'Month')
  const day = _pick(formatObject, 'day', 'Day')
  const separator = _pick(formatObject, 'separator', 'Separator') ?? ''
  if (year == null || month == null || day == null) return ''
  if (separator === '年') {
    return `${year}年${month}月${day}日`
  }
  return `${year}${separator}${month}${separator}${day}`
}

/**
 * Date_conversion mapping を画面表示用 item に変換
 *
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
function _dateFormatToItem(mappingInfo) {
  const src = _asRecord(mappingInfo.source_date_format)
  const format = _pick(src, 'format', 'Format')
  return {
    type: 'date_format',
    mapping_info: {
      MappingType: 'date_format',
      SourceDateFormat: {
        MappingFileType: _pick(src, 'mapping_file_type', 'mappingFileType') ?? '',
        MappingColumnName: _pick(src, 'mapping_column_name', 'mappingColumnName') ?? '',
        IsCustom: Boolean(_pick(src, 'is_custom', 'isCustom', 'IsCustom')),
        Format: _dateFormatToUiString(format)
      }
    }
  }
}

/**
 * Time_conversion mapping を画面表示用 item に変換
 *
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
function _timeFormatToItem(mappingInfo) {
  const src = _asRecord(mappingInfo.source_time_format)
  return {
    type: 'time_format',
    mapping_info: {
      MappingType: 'time_format',
      MappingFileType: _pick(src, 'mapping_file_type', 'mappingFileType') ?? '',
      MappingColumnName: _pick(src, 'mapping_column_name', 'mappingColumnName') ?? '',
      IsCustom: Boolean(_pick(src, 'is_custom', 'isCustom', 'IsCustom')),
      TimeFormat: {
        SourceTimeFormat: _pick(src, 'format', 'Format') ?? ''
      }
    }
  }
}

const MAPPING_TYPE_HANDLERS = {
  simple: (mappingInfo) => {
    if (mappingInfo.simple_mapping && typeof mappingInfo.simple_mapping === 'object') {
      return _simpleToItem(mappingInfo)
    }
    return { type: 'simple' }
  },
  fixed: (mappingInfo) => {
    if (mappingInfo.constant != null) {
      return _fixedToItem(mappingInfo)
    }
    return { type: 'fixed' }
  },
  conversion_table: _conversionTableToItem,
  arithmetic: _arithmeticToItem,
  condition: _conditionToItem,
  condition_multiple: _multiConditionToItem,
  value_conversion: (mappingInfo) => _valueMappingToItem(mappingInfo, 'value_conversion'),
  value_conversion_manual: (mappingInfo) => _valueMappingToItem(mappingInfo, 'value_conversion_manual'),
  date_conversion: _dateFormatToItem,
  time_conversion: _timeFormatToItem,
  remove: () => ({ type: 'remove' }),
  judgment_rule: () => ({ type: 'judgment_rule' })
}

/**
 * BE の mapping_info をフロントの item 形に変換（IF02085 → 画面用）
 *
 * @param {string} mappingType
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
export function _mappingInfoToItem(mappingType, mappingInfo) {
  if (!mappingInfo || typeof mappingInfo !== 'object') return { type: mappingType }

  const normalizedMappingInfo = _asRecord(mappingInfo)
  const handler = MAPPING_TYPE_HANDLERS[mappingType]

  return handler ? handler(normalizedMappingInfo) : { type: mappingType, mapping_info: normalizedMappingInfo }
}

/**
 * IF02085 レスポンスを mappingData / skippedFields / ruleAppliedFields に変換（SR-051）
 *
 * @param {unknown} raw
 * @returns {{
 *   mappingData: import('./types').MappingData
 *   skippedFields: import('./types').SkippedFields
 *   genericColumns: import('./types').GenericColumnWithRule[]
 *   ruleAppliedFields: Record<string, boolean>
 * }}
 */
export function _transformMappingConfigResponse(raw) {
  const mappingData = {}
  const skippedFields = {}
  const ruleAppliedFields = {}
  const list = Array.isArray(raw) ? raw : []
  const byFieldKey = new Map()
  for (const row of list) {
    const r = _snakeToCamel(/** @type {Record<string, unknown>} */ (row))
    const fieldKey = `${r.genericTaskCode ?? ''}_${r.chrofyColumnCode ?? ''}`
    if (!byFieldKey.has(fieldKey)) {
      byFieldKey.set(fieldKey, [])
    }
    byFieldKey.get(fieldKey).push(r)
  }
  const genericColumns = []
  for (const [fieldKey, rows] of byFieldKey) {
    rows.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    const first = rows[0]
    const genericTaskCode = String(first.genericTaskCode ?? '')
    const chrofyColumnCode = String(first.chrofyColumnCode ?? '')
    const hasCustomRule = rows.some((row) => row.mappingType === 'judgment_rule')
    genericColumns.push({
      genericTaskCode,
      chrofyColumnCode,
      chrofyColumnName: String(first.chrofyColumnName ?? ''),
      hasCustomRule
    })
    const onlyRemove = rows.length === 1 && first.mappingType === 'remove'
    if (onlyRemove) {
      skippedFields[fieldKey] = true
      continue
    }
    if (hasCustomRule) {
      // judgment_rule は単独行を前提（_buildMappings も exclusive 扱い）。mappingData には積まず
      // ruleAppliedFields に立てる。INIT 経路でも genericColumns[i].hasCustomRule || ruleAppliedFields の OR で表示判定が成立する
      ruleAppliedFields[fieldKey] = true
      continue
    }
    const items = rows.map((row) => {
      const item = _mappingInfoToItem(row.mappingType ?? 'simple', row.mappingInfo ?? {})
      if (row.pageState != null) item.page_state = row.pageState
      if (row.page_state != null) item.page_state = row.page_state
      return item
    })
    mappingData[fieldKey] = {
      items,
      mode: first.numAggType === 'total' ? 'sum' : 'priority'
    }
  }
  return { mappingData, skippedFields, genericColumns, ruleAppliedFields }
}

// --- 進捗まわり（SR-007）

/**
 * 進捗の分子（設定済み件数）を算出（SR-007） 次のいずれかに該当する行の件数: マッピング設定あり / スキップ済み / 判定ルール適用（hasCustomRule）
 *
 * @param {import('./types').MappingData} mappingData
 * @param {import('./types').SkippedFields} skippedFields
 * @param {Record<string, { hasCustomRule?: boolean }>} rowStateMap - FieldKey -> { hasCustomRule }
 * @param {string[]} fieldKeys - 対象範囲の fieldKey 一覧（カテゴリ未選択時は全件、選択時は当該カテゴリのみ）
 * @returns {number}
 */
export function getProgressNumerator(mappingData, skippedFields, rowStateMap, fieldKeys) {
  let count = 0
  for (const fieldKey of fieldKeys) {
    const hasMapping = mappingData[fieldKey]?.items?.length > 0
    const skipped = skippedFields[fieldKey] === true
    const hasCustomRule = rowStateMap[fieldKey]?.hasCustomRule === true
    if (hasMapping || skipped || hasCustomRule) count += 1
  }
  return count
}

/**
 * 進捗の分母（総件数）を返す（SR-007）
 *
 * @param {string[]} fieldKeys - 対象範囲の fieldKey 一覧
 * @returns {number}
 */
export function getProgressDenominator(fieldKeys) {
  return fieldKeys.length
}

/**
 * 進捗パーセント（0–100）を算出
 *
 * @param {number} numerator
 * @param {number} denominator
 * @returns {number}
 */
export function getProgressPercent(numerator, denominator) {
  if (denominator <= 0) return 0
  return Math.min(100, Math.round((numerator / denominator) * 100))
}

/**
 * マッピング item から mapping file type を取得する（前期間コピー時のフィルタ用）
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function getItemMappingFileType(item) {
  if (!item || typeof item !== 'object') return ''
  const ft = (v) => (v != null && v !== '' ? String(v) : '')
  if (item.fileType != null && item.fileType !== '') return ft(item.fileType)
  const mi = item.mapping_info
  if (mi && typeof mi === 'object') {
    const vm = mi.ValueMapping ?? mi.valueMapping ?? {}
    const sm = mi.SimpleMapping ?? mi.simpleMapping ?? {}
    const ct = mi.ConversionTable ?? mi.conversionTable ?? {}
    const sdf = mi.SourceDateFormat ?? mi.sourceDateFormat ?? {}
    return (
      ft(vm.MappingFileType ?? vm.mappingFileType) ||
      ft(sm.MappingFileType ?? sm.mappingFileType) ||
      ft(ct.MappingFileType ?? ct.mappingFileType) ||
      ft(sdf.MappingFileType ?? sdf.mapping_file_type) ||
      ft(mi.MappingFileType ?? mi.mappingFileType) ||
      ''
    )
  }
  return ''
}

/**
 * AST 引数ノード（Literal / Identifier）から値を取り出す。大文字/小文字キー両対応。
 *
 * @param {any} arg
 * @returns {unknown}
 */
function _astArgValue(arg) {
  if (!arg || typeof arg !== 'object') return null
  if ('value' in arg) return arg.value
  if ('Value' in arg) return arg.Value
  if ((arg.type ?? arg.Type) === 'Identifier') return arg.name ?? arg.Name
  return null
}

/**
 * AST を再帰探索し col(fileType, column) の [fileType, column] を acc に積む。 条件式・四則演算式・結果値は JSON 文字列で保存されているため、文字列も JSON.parse
 * して再帰する（固定値など AST でない文字列はスキップ）。 バックエンド `extract_col_filetype_column_pairs` と同等（大文字/小文字キー両対応）。
 *
 * @param {unknown} node
 * @param {[string, string][]} acc
 */
function _collectColPairs(node, acc) {
  if (node == null) return
  if (typeof node === 'string') {
    if (node === '') return
    try {
      _collectColPairs(JSON.parse(node), acc)
    } catch {
      // AST ではない文字列（固定値など）はスキップ
    }
    return
  }
  if (Array.isArray(node)) {
    node.forEach((child) => _collectColPairs(child, acc))
    return
  }
  if (typeof node !== 'object') return

  const type = node.type ?? node.Type
  if (type === 'CallExpression') {
    const callee = node.callee ?? node.Callee
    const args = node.arguments ?? node.Arguments ?? []
    const name = callee && typeof callee === 'object' ? (callee.name ?? callee.Name) : null
    if (name === 'col' && args.length >= 2) {
      const ft = _astArgValue(args[0])
      const col = _astArgValue(args[1])
      if (ft != null && col != null) acc.push([String(ft), String(col)])
    }
  }

  for (const value of Object.values(node)) {
    _collectColPairs(value, acc)
  }
}

/**
 * マッピング item が参照する col(fileType, column) ペアを全て返す（前期間コピーのスキップ判定用）。 「条件によって値を変える」「計算して求める（四則演算）」
 * 「複数の条件を組み合わせる」の条件式・結果値に埋め込まれた col() 参照を対象とする。 直値系（simple / value_conversion / conversion_table / date / time）は col()
 * を持たないため空配列を返す。
 *
 * @param {Record<string, unknown>} item
 * @returns {[string, string][]}
 */
export function getItemColFileTypePairs(item) {
  if (!item || typeof item !== 'object') return []
  const acc = []
  _collectColPairs(item.mapping_info, acc)
  return acc
}

/**
 * `item.type` -> `mapping_info` 内で列参照を保持するキー。
 *
 * バックエンド `copy_previous_mapping_bulk` の `_MAPPING_TYPE_INFO_KEY` に対応する。 ここに無い type（fixed / 条件分岐 / 四則演算
 * など）は直値の列参照を持たないため 判定対象外＝常にコピー可とする（バックエンドが `None` を返すのと同じ挙動）。
 */
const _DIRECT_REF_INFO_KEYS = {
  simple: ['SimpleMapping', 'simpleMapping'],
  conversion_table: ['ConversionTable', 'conversionTable'],
  ValueMapping: ['ValueMapping', 'valueMapping'],
  value_mapping: ['ValueMapping', 'valueMapping'],
  value_mapping_dnd: ['ValueMapping', 'valueMapping'],
  date_format: ['SourceDateFormat', 'sourceDateFormat'],
  time_format: ['SourceTimeFormat', 'sourceTimeFormat']
}

/**
 * マッピング item が直値で参照する `(ファイル種別, 列)` ペアを返す（前期間コピーのスキップ判定用）。
 *
 * 「そのまま取り込む」「変換表」「値変換」「日付形式」「時間形式」は条件式を持たず `MappingColumnName` で列を直接参照するため、`getItemColFileTypePairs`（col() AST
 * 探索）では検出できない。 バックエンド `_get_filetype_column_for_mapping` と同一判定にするため、 列は item 直下を優先、ファイル種別は nested のみ、nested 欠落時は
 * `mapping_info` 自体にフォールバックし、 正規化は trim のみとする（全角半角変換などを行うとバックエンドと判定がずれる）。
 *
 * @param {Record<string, unknown>} item
 * @returns {[string, string][]}
 */
export function getItemDirectColumnRefs(item) {
  if (!item || typeof item !== 'object') return []
  const keys = _DIRECT_REF_INFO_KEYS[/** @type {string} */ (item.type)]
  if (!keys) return []

  const mappingInfo = _asRecord(item.mapping_info)
  const nestedRaw = keys.map((key) => mappingInfo[key]).find((value) => value != null && typeof value === 'object')
  const nested = _asRecord(nestedRaw ?? mappingInfo)

  // `_simpleToItem` のように `mapping_info` を持たず `item.fileType` / `item.field` だけを
  // 返す item もあるため、ファイル種別は `getItemMappingFileType`（item 直下と nested の
  // 双方に対応）で解決する。nested のみを見ると空文字になり、参照先を解決できなくなる。
  const fileType = getItemMappingFileType(item).trim()
  const column = String(
    _pick(item, 'field') ?? _pick(nested, 'MappingColumnName', 'mappingColumnName', 'mapping_column_name') ?? ''
  ).trim()

  if (!fileType && !column) return []
  return [[fileType, column]]
}

/**
 * 値変換 item から表示用 fileType / field を解決する
 *
 * @param {Record<string, unknown>} item
 * @returns {{ fileType: string; field: string }}
 */
export function getItemFileTypeAndField(item) {
  const valueMapping = item?.mapping_info?.ValueMapping ?? {}
  return {
    fileType: (item?.fileType ?? valueMapping.MappingFileType ?? valueMapping.mappingFileType ?? '').toString(),
    field: (item?.field ?? valueMapping.MappingColumnName ?? valueMapping.mappingColumnName ?? '').toString()
  }
}

/**
 * ConversionTableKey (ID) から conversionTableName を解決する
 *
 * @param {string} conversionTableKey
 * @param {{ conversionTableKey?: string; conversionTableName?: string }[]} [conversionTableList]
 * @returns {string}
 */
function _resolveConversionTableName(conversionTableKey, conversionTableList) {
  if (!conversionTableKey || !Array.isArray(conversionTableList)) return conversionTableKey
  const found = conversionTableList.find(
    (t) => String(t?.conversionTableKey ?? t?.conversion_table_key ?? '') === conversionTableKey
  )
  return (found?.conversionTableName ?? found?.conversion_table_name ?? conversionTableKey).toString()
}

/**
 * マッピング item の一覧表示ラベルを生成する
 *
 * @param {Record<string, unknown>} item
 * @param {{ conversionTableList?: { conversionTableKey?: string; conversionTableName?: string }[] }} [options]
 * @returns {string}
 */
export function formatMappingItemLabel(item, options = {}) {
  if (!item || typeof item !== 'object') return '設定'
  const type = item.type
  if (type === 'simple') {
    const simpleMapping = item.mapping_info?.SimpleMapping ?? {}
    const fileType = (item.fileType ?? simpleMapping.MappingFileType ?? simpleMapping.mappingFileType ?? '').toString()
    const field = (item.field ?? simpleMapping.MappingColumnName ?? simpleMapping.mappingColumnName ?? '').toString()
    return `📋 ${fileType} の ${field} をそのまま取り込む`
  }
  if (type === 'fixed' || type === 'constant') return `固定値: ${item.value ?? item.constant ?? ''}`
  if (type === 'conversion_table') {
    const conversionTable = item.mapping_info?.ConversionTable ?? item.mapping_info ?? {}
    const fileType = (
      item.fileType ??
      conversionTable.MappingFileType ??
      conversionTable.mappingFileType ??
      conversionTable.mapping_file_type ??
      ''
    ).toString()
    const field = (
      item.field ??
      conversionTable.MappingColumnName ??
      conversionTable.mappingColumnName ??
      conversionTable.mapping_column_name ??
      ''
    ).toString()
    const conversionTableKey = (
      item.conversionTableKey ??
      conversionTable.ConversionTableKey ??
      conversionTable.conversionTableKey ??
      conversionTable.conversion_table_key ??
      ''
    ).toString()
    const conversionTableName = _resolveConversionTableName(conversionTableKey, options.conversionTableList)
    if (conversionTableName) {
      return `🔄 変換表: ${fileType && field ? `${fileType} の ${field} — ` : ''}${conversionTableName}`
    }
    return `🔄 変換表${fileType && field ? `: ${fileType} の ${field}` : ''}`
  }
  if (type === 'ValueMapping' || type === 'value_mapping' || type === 'value_mapping_dnd') {
    const { fileType, field } = getItemFileTypeAndField(item)
    return `🔁 値変換: ${fileType} の ${field}`
  }
  if (type === 'date_format') return '📅 日付形式設定'
  if (type === 'time_format') return '⏰ 時間形式変換'
  if (type === 'FourArithmeticExpressions') return '🧮 四則演算'
  if (type === 'SingleCondition') return '🔀 条件設定（単一）'
  if (type === 'multi_condition') return '🔀 条件設定（複数）'
  return `設定 (${type})`
}

/**
 * 進捗の設定済み件数を算出する
 *
 * @param {Record<string, { items?: unknown[] }>} mappingData
 * @param {Record<string, boolean>} skippedFields
 * @param {{ genericTaskCode?: string; chrofyColumnCode?: string; fieldKey?: string; hasCustomRule?: boolean }[]} genericColumns
 * @param {string | null} currentCategory
 * @param {Record<string, boolean>} [ruleAppliedFields]
 * @returns {number}
 */
export function getProgressDoneCount(
  mappingData,
  skippedFields,
  genericColumns,
  currentCategory,
  ruleAppliedFields = {}
) {
  const rows = currentCategory
    ? genericColumns.filter((column) => String(column.genericTaskCode) === currentCategory)
    : genericColumns
  let count = 0
  for (const column of rows) {
    const fieldKey = column.fieldKey ?? `${column.genericTaskCode}_${column.chrofyColumnCode}`
    if (skippedFields[fieldKey]) count += 1
    else if (mappingData[fieldKey]?.items?.length) count += 1
    else if (column.hasCustomRule || ruleAppliedFields[fieldKey]) count += 1
  }
  return count
}

/**
 * Issue 一覧から rowNumber の Set を生成する
 *
 * @param {{ rowNumber?: number | string | null }[]} issueList
 * @returns {Set<number | string>}
 */
export function getValidationIssueRowNumberSet(issueList) {
  return new Set((issueList ?? []).map((issue) => issue.rowNumber).filter((rowNumber) => rowNumber != null))
}

/**
 * Issue 一覧から row-column の Set を生成する
 *
 * @param {{ rowNumber?: number | string | null; columnName?: string | null }[]} issueList
 * @returns {Set<string>}
 */
export function getValidationIssueCellKeySet(issueList) {
  const set = new Set()
  ;(issueList ?? []).forEach((issue) => {
    if (issue.rowNumber != null && issue.columnName != null) {
      set.add(`${issue.rowNumber}-${issue.columnName}`)
    }
  })
  return set
}

/**
 * 行番号が「特定のデータ行」を表す正の整数かどうかを判定する
 *
 * 行に紐づかない列単位エラー（バグ#3095 の区分マスター未設定など）は バックエンドで行番号 0 として返るため、正の整数でないものは行なしと扱う。
 *
 * @param {number | string | null | undefined} rowNumber
 * @returns {boolean}
 */
export function isPositiveRowNumber(rowNumber) {
  const n = Number(rowNumber)
  return Number.isInteger(n) && n >= 1
}

/**
 * 検証詳細一覧で rowNumber / columnName に一致する issue の index を返す
 *
 * 列単位エラーは rowNumber が正の整数でないため、行セルクリック時は列名でのフォールバック照合を行う。
 *
 * @param {{ rowNumber?: number | string | null; columnName?: string | null }[]} issueList
 * @param {number | string | null | undefined} rowNumber
 * @param {string | null | undefined} columnName
 * @returns {number}
 */
export function findValidationDetailIndex(issueList, rowNumber, columnName) {
  const list = issueList ?? []
  const exactIdx = list.findIndex((e) => e.rowNumber === rowNumber && e.columnName === columnName)
  if (exactIdx >= 0) return exactIdx
  if (columnName == null) return -1
  return list.findIndex((e) => e.columnName === columnName && !isPositiveRowNumber(e.rowNumber))
}

/**
 * マッピング結果プレビューでセルがハイライト対象かどうかを判定する
 *
 * @param {{ rowNumber?: number | string | null; columnName?: string | null; columnLevel?: boolean } | null} highlightedCell
 * @param {number | string | null | undefined} rowNumber
 * @param {string | null | undefined} columnName
 * @returns {boolean}
 */
export function isValidationCellHighlighted(highlightedCell, rowNumber, columnName) {
  if (!highlightedCell || columnName == null) return false
  if (highlightedCell.columnLevel) {
    return highlightedCell.columnName === columnName
  }
  return highlightedCell.rowNumber === rowNumber && highlightedCell.columnName === columnName
}

/**
 * Issue 一覧から「列単位エラー」（特定の行に紐づかない）の columnName Set を生成する
 *
 * 行番号が正の整数でない（=特定行に紐づかない）issue を列単位エラーとみなす。 区分マスター（ユーザー設定）未設定エラーのように、列全体が対象となる エラーをマッピング結果プレビューで列全体ハイライトするために使用する。
 *
 * @param {{ rowNumber?: number | string | null; columnName?: string | null }[]} issueList
 * @returns {Set<string>}
 */
export function getValidationIssueColumnLevelSet(issueList) {
  const set = new Set()
  ;(issueList ?? []).forEach((issue) => {
    if (issue.columnName != null && !isPositiveRowNumber(issue.rowNumber)) {
      set.add(issue.columnName)
    }
  })
  return set
}

/**
 * 検証詳細一覧の「行」列の表示用に行番号を整形する
 *
 * 特定の行に紐づかないエラー（行番号が正の整数でない）は "-" を返す。
 *
 * @param {number | string | null | undefined} rowNumber
 * @returns {string}
 */
export function formatValidationRowNumber(rowNumber) {
  return isPositiveRowNumber(rowNumber) ? String(Number(rowNumber)) : '-'
}

/**
 * 検証結果行が「エラーのみ」「警告のみ」フィルタに含まれるかどうかを判定する
 *
 * 列単位エラー/警告は特定行に紐づかないため、該当列が1つでもあれば全行を表示対象とする。
 *
 * @param {number | string | null | undefined} rowNumber
 * @param {'all' | 'error' | 'warning'} currentFilter
 * @param {{ rowNumber?: number | string | null }[]} errorDetailsList
 * @param {{ rowNumber?: number | string | null }[]} warningDetailsList
 * @returns {boolean}
 */
export function matchesValidationRowFilter(rowNumber, currentFilter, errorDetailsList, warningDetailsList) {
  if (currentFilter === 'all') return true
  if (currentFilter === 'error') {
    if (getValidationIssueColumnLevelSet(errorDetailsList).size > 0) return true
    return getValidationIssueRowNumberSet(errorDetailsList).has(rowNumber)
  }
  if (currentFilter === 'warning') {
    if (getValidationIssueColumnLevelSet(warningDetailsList).size > 0) return true
    return getValidationIssueRowNumberSet(warningDetailsList).has(rowNumber)
  }
  return true
}

/**
 * Validation 行を filter 条件で絞り込む
 *
 * @param {{ rowNumber?: number | string | null }[]} rows
 * @param {'all' | 'error' | 'warning'} currentFilter
 * @param {{ rowNumber?: number | string | null }[]} errorDetailsList
 * @param {{ rowNumber?: number | string | null }[]} warningDetailsList
 * @returns {{ rowNumber?: number | string | null }[]}
 */
export function filterValidationRows(rows, currentFilter, errorDetailsList, warningDetailsList) {
  if (currentFilter === 'all') return rows ?? []
  return (rows ?? []).filter((row) =>
    matchesValidationRowFilter(row.rowNumber, currentFilter, errorDetailsList, warningDetailsList)
  )
}

/**
 * 画面表示用 item から編集モーダル用 MappingInfo を再構築する
 *
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown> | null}
 */
export function buildInitialMappingInfoFromItem(item) {
  if (!item || typeof item !== 'object') return null

  if (item.type === 'simple') {
    const simpleMapping = item.mapping_info?.SimpleMapping ?? item.mapping_info?.simpleMapping ?? {}
    return {
      MappingType: 'SimpleMapping',
      SimpleMapping: {
        MappingFileType: item.fileType ?? simpleMapping.MappingFileType ?? simpleMapping.mappingFileType ?? '',
        MappingColumnName: item.field ?? simpleMapping.MappingColumnName ?? simpleMapping.mappingColumnName ?? ''
      }
    }
  }
  if (item.type === 'fixed' || item.type === 'constant') {
    return {
      MappingType: 'Constant',
      Constant: item.constant ?? item.value ?? item.mapping_info?.Constant ?? item.mapping_info?.constant ?? ''
    }
  }
  if (item.type === 'conversion_table') {
    const conversionTable =
      item.mapping_info?.ConversionTable ?? item.mapping_info?.conversionTable ?? item.mapping_info ?? {}
    return {
      MappingType: 'ConversionTable',
      ConversionTable: {
        MappingFileType:
          item.fileType ??
          conversionTable.MappingFileType ??
          conversionTable.mappingFileType ??
          conversionTable.mapping_file_type ??
          '',
        MappingColumnName:
          item.field ??
          conversionTable.MappingColumnName ??
          conversionTable.mappingColumnName ??
          conversionTable.mapping_column_name ??
          '',
        ConversionTableKey:
          item.conversionTableKey ??
          conversionTable.ConversionTableKey ??
          conversionTable.conversionTableKey ??
          conversionTable.conversion_table_key ??
          ''
      }
    }
  }
  if (item.type === 'value_mapping' || item.type === 'value_mapping_dnd' || item.type === 'ValueMapping') {
    const valueMapping =
      item.mapping_info?.ValueMapping ?? item.mapping_info?.valueMapping ?? item.mapping_info?.value_mapping ?? {}
    const mappingList =
      valueMapping.MappingList ??
      valueMapping.mappingList ??
      valueMapping.mapping_list ??
      item.mapping_info?.MappingList ??
      item.mapping_info?.mappingList ??
      []
    return {
      MappingType: 'ValueMapping',
      _valueMappingUiType: item.type === 'value_mapping' ? 'manual' : 'dnd',
      ValueMapping: {
        MappingFileType:
          item.fileType ??
          valueMapping.MappingFileType ??
          valueMapping.mappingFileType ??
          valueMapping.mapping_file_type ??
          '',
        MappingColumnName:
          item.field ??
          valueMapping.MappingColumnName ??
          valueMapping.mappingColumnName ??
          valueMapping.mapping_column_name ??
          '',
        MappingList: Array.isArray(mappingList) ? mappingList : []
      }
    }
  }
  if (item.mapping_info && typeof item.mapping_info === 'object') return item.mapping_info
  return null
}

/**
 * 各モーダルフックの onApply が返す MappingType ネスト形式を _itemToMappingInfo が期待するフラット形式に変換する
 *
 * @param {Record<string, unknown>} mappingInfo
 * @returns {Record<string, unknown>}
 */
export function _mappingInfoToStoreItem(mappingInfo) {
  const normalizedMappingInfo = _asRecord(mappingInfo)
  const mt = _pick(normalizedMappingInfo, 'MappingType', 'mappingType') ?? ''
  if (mt === 'SimpleMapping') {
    const sm = _asRecord(normalizedMappingInfo.SimpleMapping)
    return {
      type: 'simple',
      fileType: sm.MappingFileType,
      field: sm.MappingColumnName
    }
  }
  if (mt === 'Constant') {
    return {
      type: 'fixed',
      value: normalizedMappingInfo.Constant,
      constant: normalizedMappingInfo.Constant
    }
  }
  if (mt === 'ConversionTable') {
    const ct = _asRecord(normalizedMappingInfo.ConversionTable)
    return {
      type: 'conversion_table',
      fileType: ct.MappingFileType,
      field: ct.MappingColumnName,
      conversionTableKey: ct.ConversionTableKey
    }
  }
  if (mt === 'ValueMapping') {
    const vm = _asRecord(normalizedMappingInfo.ValueMapping)
    const valueMappingUiType =
      normalizedMappingInfo._valueMappingUiType === 'manual'
        ? 'manual'
        : normalizedMappingInfo._valueMappingUiType === 'dnd' || normalizedMappingInfo._isDnd === true
          ? 'dnd'
          : null
    return {
      type:
        valueMappingUiType === 'manual'
          ? 'value_mapping'
          : valueMappingUiType === 'dnd'
            ? 'value_mapping_dnd'
            : 'ValueMapping',
      fileType: _pick(vm, 'MappingFileType', 'mappingFileType'),
      field: _pick(vm, 'MappingColumnName', 'mappingColumnName'),
      mapping_info: normalizedMappingInfo
    }
  }
  return { type: mt, mapping_info: normalizedMappingInfo }
}

// --- Save payload (FE → BE, IF02087)

/**
 * 保存用の AST 文字列に正規化する
 *
 * @param {unknown} value
 * @returns {string}
 */
export function toStoredAstString(value) {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value ?? '')
}

/**
 * 日付形式の UI 文字列を IF02087 用 format に変換
 *
 * @param {unknown} value
 * @returns {string | Record<string, string>}
 */
export function toDateFormatPayload(value) {
  const format = String(value ?? '').trim()
  if (!format) return ''
  // 要件: date_format の `Format` は文字列のまま送る（分解しない）
  // BE 側で文字列フォーマットとして解釈される前提のため、ここでは正規化のみ行う。
  return format
}

/** FE の item.type を BE の mapping_type（IF02087）に変換 */
const FE_TYPE_TO_BE = {
  simple: 'simple',
  fixed: 'fixed',
  conversion_table: 'conversion_table',
  FourArithmeticExpressions: 'arithmetic',
  SingleCondition: 'condition',
  multi_condition: 'condition_multiple',
  ValueMapping: 'value_conversion',
  value_mapping: 'value_conversion_manual',
  value_mapping_dnd: 'value_conversion',
  date_format: 'date_conversion',
  time_format: 'time_conversion',
  remove: 'remove',
  judgment_rule: 'judgment_rule'
}

/**
 * @param {unknown} feType
 * @returns {string}
 */
export function feTypeToBeMappingType(feType) {
  return FE_TYPE_TO_BE[String(feType)] ?? String(feType)
}

/**
 * 単一条件行を BE 送信用に正規化
 *
 * @param {Record<string, unknown>} rawRow
 * @param {unknown} formState
 * @returns {Record<string, unknown>}
 */
function _singleConditionRowToPayload(rawRow, formState) {
  const rowOut = _omitKeys(rawRow, [
    'conditionalExpression',
    'ConditionalExpression',
    'conditional_expression',
    'condition',
    'formState',
    'form_state'
  ])
  rowOut.conditional_expression = toStoredAstString(
    _pick(rawRow, 'conditionalExpression', 'ConditionalExpression', 'conditional_expression', 'condition') ?? ''
  )
  rowOut.value = toStoredAstString(_pick(rawRow, 'value', 'Value') ?? '')
  rowOut.form_state = _pick(rawRow, 'formState', 'form_state') ?? formState ?? null
  return rowOut
}

/**
 * 複数条件行を BE 送信用に正規化
 *
 * @param {unknown} row
 * @param {unknown} fallbackFormState
 * @returns {Record<string, unknown>}
 */
function _multiConditionRowToPayload(row, fallbackFormState) {
  const rawRow = _asRecord(row)
  const out = _omitKeys(rawRow, ['conditionSet', 'ConditionSet', 'condition_set', 'value', 'Value'])

  let rawConditionSets = _pickArray(rawRow, 'conditionSet', 'ConditionSet', 'condition_set')
  if (
    rawConditionSets.length === 0 &&
    _pickArray(rawRow, 'ConditionalExpressionList', 'conditionalExpressionList').length > 0
  ) {
    rawConditionSets = [
      {
        joinCondition: _pick(rawRow, 'joinCondition', 'JoinCondition') ?? 'and',
        conditionalExpressionList: _pick(rawRow, 'ConditionalExpressionList', 'conditionalExpressionList')
      }
    ]
  }

  out.condition_set = rawConditionSets.map((conditionSet) => {
    const rawSet = _asRecord(conditionSet)
    const setOut = _omitKeys(rawSet, [
      'joinCondition',
      'JoinCondition',
      'join_condition',
      'conditionalExpressionList',
      'ConditionalExpressionList',
      'conditional_expression_list'
    ])
    setOut.join_condition = String(_pick(rawSet, 'joinCondition', 'JoinCondition', 'join_condition') ?? 'and')
    const exprList = _pickArray(
      rawSet,
      'conditionalExpressionList',
      'ConditionalExpressionList',
      'conditional_expression_list'
    )
    setOut.conditional_expression_list = exprList.map((expr) => {
      const rawExpr = _asRecord(expr)
      const exprOut = _omitKeys(rawExpr, [
        'conditionalExpression',
        'ConditionalExpression',
        'conditional_expression',
        'formState',
        'form_state'
      ])
      exprOut.conditional_expression = toStoredAstString(
        _pick(rawExpr, 'conditionalExpression', 'ConditionalExpression', 'conditional_expression') ?? expr
      )
      exprOut.form_state = _pick(rawExpr, 'formState', 'form_state') ?? fallbackFormState ?? null
      return exprOut
    })
    return setOut
  })

  out.value = toStoredAstString(_pick(rawRow, 'value', 'Value') ?? '')
  return out
}

/**
 * 画面の mappingData 1件を BE の mapping_info に変換（IF02087）
 *
 * @param {Record<string, unknown>} [item]
 * @returns {Record<string, unknown>}
 */
export function itemToMappingInfoForPayload(item) {
  if (!item || typeof item !== 'object') return {}
  const type = item.type
  if (type === 'remove') return { mapping_type: 'remove' }
  if (type === 'judgment_rule') return {}
  if (type === 'conversion_table' && item.conversionTableKey != null) {
    const conversionTable = _asRecord(_pick(item, 'mapping_info') ?? {})
    const nestedConversionTable = _asRecord(
      _pick(conversionTable, 'ConversionTable', 'conversionTable', 'conversion_table')
    )
    const mappingFileType =
      _pick(nestedConversionTable, 'MappingFileType', 'mappingFileType', 'mapping_file_type') ??
      _pick(item, 'fileType') ??
      ''
    const mappingColumnName =
      _pick(nestedConversionTable, 'MappingColumnName', 'mappingColumnName', 'mapping_column_name') ??
      _pick(item, 'field') ??
      ''
    const conversionTableKey =
      _pick(nestedConversionTable, 'ConversionTableKey', 'conversionTableKey', 'conversion_table_key') ??
      _pick(item, 'conversionTableKey') ??
      ''
    return {
      conversion_table: {
        mapping_file_type: String(mappingFileType ?? ''),
        mapping_column_name: String(mappingColumnName ?? ''),
        conversion_table_key: String(conversionTableKey ?? '')
      }
    }
  }
  if (type === 'fixed' && (item.value != null || item.constant != null)) {
    return { constant: item.constant ?? item.value }
  }
  if (type === 'simple' && (item.fileType != null || item.field != null)) {
    return {
      simple_mapping: {
        mapping_file_type: item.fileType ?? '',
        mapping_column_name: item.field ?? ''
      }
    }
  }
  const mi = item.mapping_info && typeof item.mapping_info === 'object' ? _asRecord(item.mapping_info) : null
  if (!mi) return {}
  const mt = String(_pick(mi, 'mappingType', 'MappingType') ?? '')
  const formState = _pick(mi, 'formState', 'form_state') ?? null

  if (mt === 'FourArithmeticExpressions') {
    const fa = _asRecord(_pick(mi, 'fourArithmeticExpressions', 'FourArithmeticExpressions'))
    return {
      four_arithmetic_expressions: toStoredAstString(_pick(fa, 'expressionAst', 'ExpressionAst') ?? ''),
      form_state: formState
    }
  }
  if (mt === 'SingleCondition') {
    const rows = _pickArray(mi, 'singleCondition', 'SingleCondition')
    return {
      single_condition: rows.map((row) => _singleConditionRowToPayload(_asRecord(row), formState))
    }
  }
  if (mt === 'multi_condition') {
    const rows = _pickArray(mi, 'multiCondition', 'MultiCondition')
    return {
      multi_condition: rows.map((row) => _multiConditionRowToPayload(row, formState))
    }
  }
  if (mt === 'ValueMapping') {
    const vm = _asRecord(_pick(mi, 'ValueMapping', 'valueMapping'))
    const rawList = _pick(vm, 'MappingList', 'mappingList') ?? _pick(mi, 'MappingList', 'mappingList')
    const mappingList = Array.isArray(rawList) ? rawList : []
    return {
      value_mapping: {
        mapping_file_type: _pick(vm, 'MappingFileType', 'mappingFileType') ?? '',
        mapping_column_name: _pick(vm, 'MappingColumnName', 'mappingColumnName') ?? '',
        mapping_list: mappingList
      }
    }
  }
  if (mt === 'date_format') {
    const sourceDateFormat = _asRecord(_pick(mi, 'SourceDateFormat', 'sourceDateFormat'))
    return {
      source_date_format: {
        mapping_file_type: _pick(sourceDateFormat, 'MappingFileType', 'mapping_file_type') ?? '',
        mapping_column_name: _pick(sourceDateFormat, 'MappingColumnName', 'mapping_column_name') ?? '',
        is_custom: Boolean(_pick(sourceDateFormat, 'IsCustom', 'is_custom') ?? false),
        format: toDateFormatPayload(_pick(sourceDateFormat, 'Format', 'format') ?? '')
      }
    }
  }
  if (mt === 'time_format') {
    const timeFormat = _asRecord(_pick(mi, 'TimeFormat', 'timeFormat'))
    return {
      source_time_format: {
        mapping_file_type: _pick(mi, 'MappingFileType', 'mappingFileType') ?? '',
        mapping_column_name: _pick(mi, 'MappingColumnName', 'mappingColumnName') ?? '',
        is_custom: Boolean(_pick(mi, 'IsCustom', 'isCustom') ?? false),
        format: _pick(timeFormat, 'SourceTimeFormat', 'sourceTimeFormat') ?? ''
      }
    }
  }
  return mi
}

// --- IF02099 汎用マッピングプレビュー取得（モーダルプレビュー）

/**
 * IF02099 汎用マッピングプレビュー取得のリクエストボディを組み立てる
 *
 * @param {{
 *   dataCreateTaskKey: string | number
 *   dataKindCode: string
 *   chrofyColumnCode: string
 *   chrofyColumnName: string
 *   storeItem: Record<string, unknown>
 *   limit?: number
 *   priority?: number
 * }} params
 * @returns {Record<string, unknown>}
 */
export function buildIf02099MappingPreviewBody(params) {
  const {
    dataCreateTaskKey,
    dataKindCode,
    chrofyColumnCode,
    chrofyColumnName,
    storeItem,
    limit = 5,
    priority = 1
  } = params

  const item = storeItem && typeof storeItem === 'object' ? storeItem : {}
  const mapping_type = feTypeToBeMappingType(item.type)
  const rawInfo = itemToMappingInfoForPayload(item)
  const mapping_info = { ...rawInfo }
  if (Object.prototype.hasOwnProperty.call(mapping_info, 'mapping_type')) {
    delete mapping_info.mapping_type
  }

  const nLimit = typeof limit === 'number' && Number.isFinite(limit) ? Math.trunc(limit) : 5
  const clampedLimit = Math.min(100, Math.max(1, nLimit))

  return {
    data_create_task_key: Number(dataCreateTaskKey),
    data_kind_code: String(dataKindCode ?? ''),
    chrofy_column_code: String(chrofyColumnCode ?? ''),
    chrofy_column_name: String(chrofyColumnName ?? ''),
    mapping_type,
    mapping_info,
    priority: priority ?? 1,
    limit: clampedLimit
  }
}

/**
 * IF02099 の列指向レスポンス（動的キー + errors）をモーダルテーブル用の行配列へ変換する
 *
 * @param {unknown} responseBody
 * @returns {Record<string, unknown>[]}
 */
export function if02099PreviewColumnResponseToRows(responseBody) {
  const body = responseBody && typeof responseBody === 'object' && !Array.isArray(responseBody) ? responseBody : {}
  /** @type {[string, unknown[]][]} */
  const arrayEntries = []
  for (const [key, val] of Object.entries(body)) {
    if (key === 'errors' || key === 'warnings') continue
    if (!Array.isArray(val)) continue
    arrayEntries.push([key, val])
  }
  if (arrayEntries.length === 0) return []
  const rowCount = Math.max(...arrayEntries.map(([, arr]) => arr.length), 0)
  const rows = []
  for (let i = 0; i < rowCount; i += 1) {
    const row = {}
    for (const [key, arr] of arrayEntries) {
      const cell = arr[i]
      row[key] = cell === undefined || cell === null ? '' : cell
    }
    rows.push(row)
  }
  return rows
}

/**
 * プレビュー API レスポンスをテーブル行へ正規化（IF02099 列形式を優先、従来の dataMappingPreview 行配列にも対応）
 *
 * @param {unknown} res
 * @returns {Record<string, unknown>[]}
 */
export function mappingPreviewApiResponseToRows(res) {
  if (!res || typeof res !== 'object') return []
  const legacy = res.dataMappingPreview
  if (Array.isArray(legacy)) return legacy
  return if02099PreviewColumnResponseToRows(res)
}

/**
 * IF02099 レスポンスの issues 配列（errors / warnings）を表示用に正規化する
 *
 * @param {unknown} res
 * @param {'errors' | 'warnings'} field
 * @returns {{ row: number | string; message: string; column: string }[]}
 */
function normalizeIf02099PreviewIssues(res, field) {
  if (!res || typeof res !== 'object' || Array.isArray(res)) return []
  const raw = /** @type {Record<string, unknown>} */ (res)[field]
  if (!Array.isArray(raw)) return []
  const out = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const o = /** @type {Record<string, unknown>} */ (item)
    const row = o.row ?? o.Row
    const message = o.message ?? o.Message ?? ''
    const column = o.column ?? o.Column ?? ''
    out.push({
      row: typeof row === 'number' && Number.isFinite(row) ? row : String(row ?? ''),
      message: String(message),
      column: String(column)
    })
  }
  return out
}

export function formatIf02099PreviewIssueHead(row, column, fallbackColumnName = '') {
  const rowLabel = `行 ${formatValidationRowNumber(row)}`
  const col = String(column ?? '').trim() || String(fallbackColumnName ?? '').trim()
  const colPart = col ? `列: ${col}` : ''
  return [rowLabel, colPart].filter(Boolean).join(' / ')
}

/**
 * IF02099 プレビュー issue を検証表示用 issue 形式へ変換する
 *
 * 列単位 issue（row が正の整数でない）で column が空のときは fallbackColumnName を列名に使う。
 *
 * @param {{ row?: number | string; column?: string }[]} issues
 * @param {string} [fallbackColumnName]
 * @returns {{ rowNumber: number | string; columnName: string }[]}
 */
export function mapIf02099PreviewIssuesToValidationIssues(issues, fallbackColumnName = '') {
  return (issues ?? []).map((item) => {
    const rowNumber = item?.row
    const rawColumn = item?.column ?? ''
    const isColumnLevel = !isPositiveRowNumber(rowNumber)
    const columnName =
      isColumnLevel && String(rawColumn).trim() === '' && fallbackColumnName ? fallbackColumnName : String(rawColumn)
    return { rowNumber, columnName }
  })
}

/**
 * IF02099 レスポンスの errors を表示用に正規化する（snake_case / camelCase 両対応）
 *
 * @param {unknown} res
 * @returns {{ row: number | string; message: string; column: string }[]}
 */
export function normalizeIf02099PreviewErrors(res) {
  return normalizeIf02099PreviewIssues(res, 'errors')
}

/**
 * IF02099 レスポンスの warnings を表示用に正規化する（snake_case / camelCase 両対応）
 *
 * @param {unknown} res
 * @returns {{ row: number | string; message: string; column: string }[]}
 */
export function normalizeIf02099PreviewWarnings(res) {
  return normalizeIf02099PreviewIssues(res, 'warnings')
}

/**
 * API レスポンスから生のエラーメッセージ文字列を抽出する
 *
 * @param {unknown} error
 * @returns {string}
 */
function extractRawApiErrorMessage(error) {
  if (!error || typeof error !== 'object') return ''
  const err = /** @type {{ message?: string; response?: Record<string, unknown> }} */ (error)
  const response = err.response
  const data = response?.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const body = /** @type {Record<string, unknown>} */ (data).body
    if (body && typeof body === 'object' && body.message != null) {
      return _normalizeErrorMessage(body.message, '')
    }
    if (data.message != null) {
      return _normalizeErrorMessage(data.message, '')
    }
  }
  if (typeof data === 'string' && data.trim()) return data
  const responseBody = response?.body
  if (responseBody && typeof responseBody === 'object' && responseBody.message != null) {
    return _normalizeErrorMessage(responseBody.message, '')
  }
  if (typeof err.message === 'string' && err.message.trim()) return err.message
  return ''
}

/** UploadFileMappedData 未登録時の API エラー識別子（IF02099） */
const PREVIEW_API_ERROR_NO_MAPPED_DATA = 'マッピングデータが存在しません'

/** プレビュー API エラー: UploadFileMappedData 未登録 */
export const PREVIEW_ERROR_NO_REGISTERED_DATA = 'プレビューデータが登録されていません。しばらく後に再度お試しください。'

/** プレビュー API エラー: その他（ネットワーク系等） */
export const PREVIEW_ERROR_GENERIC = 'プレビューデータ取得時にエラーが発生しました。'

/**
 * IF02099 プレビュー API 失敗時の画面表示用メッセージを返す
 *
 * @param {unknown} error
 * @returns {string}
 */
export function resolveMappingPreviewApiErrorMessage(error) {
  const raw = extractRawApiErrorMessage(error)
  if (raw.includes(PREVIEW_API_ERROR_NO_MAPPED_DATA)) {
    return PREVIEW_ERROR_NO_REGISTERED_DATA
  }
  return PREVIEW_ERROR_GENERIC
}

/**
 * API 呼び出し失敗時のエラーメッセージを取得する
 *
 * @param {unknown} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function resolveApiErrorMessage(error, fallback = 'プレビューの取得に失敗しました。') {
  const raw = extractRawApiErrorMessage(error)
  if (raw) return raw
  return fallback
}

/**
 * @param {unknown} message
 * @param {string} fallback
 * @returns {string}
 */
function _normalizeErrorMessage(message, fallback) {
  if (Array.isArray(message)) {
    const joined = message.filter(Boolean).join('\n')
    return joined || fallback
  }
  if (typeof message === 'string' && message.trim()) return message
  return fallback
}
